import {
    DoorOpenIcon,
    DoorClosedIcon,
    AlarmClockIcon,
    WarningIcon,
    ClockIcon,
    ArchiveIcon,
    ChatBubbleDotRegularIcon,
    ChatBubbleQuestionRegularIcon,
    ChatBubbleSlashRegularIcon,
} from 'shells/icon';

import type {OriginType, ThreadType} from 'nutshell-graphql-types';
import type {
    GetInboxThreadCounts_inboxThreadCounts as InboxThreadCounts,
    GetInboxThreadCounts_inboxThreadCounts_chat as InboxThreadCountsChat,
    GetInboxThreadCounts_inboxThreadCounts_sms as InboxThreadCountsSms,
    GetInboxThreadCounts_inboxThreadCounts_messenger as InboxThreadCountsMessenger,
    GetInboxThreadCounts_inboxThreadCounts_instagram as InboxThreadCountsInstagram,
    GetInboxThreadCounts_inboxThreadCounts_allChannels as InboxThreadCountsAllChannels,
    GetInboxThreadCounts_inboxThreadCounts_whatsapp as InboxThreadCountsWhatsApp,
    GetInboxThreadCounts_inboxThreadCounts_email as InboxThreadCountsEmail,
    GetInboxThreadCounts_inboxThreadCounts_chatbot as InboxThreadCountsChatbot,
} from 'nutshell-graphql-types';

const THREAD_STATUS_OPTIONS: {
    label: string;
    value: string;
    icon: React.ComponentType<any>;
}[] = [
    {label: 'Open', value: 'OPEN', icon: DoorOpenIcon},
    {label: 'Closed', value: 'CLOSED', icon: DoorClosedIcon},
    {label: 'Scheduled', value: 'SCHEDULED', icon: AlarmClockIcon},
    {label: 'Snoozed', value: 'SNOOZED', icon: ClockIcon},
    {label: 'Failed to send', value: 'FAILED', icon: WarningIcon},
    {label: 'Archived', value: 'ARCHIVED', icon: ArchiveIcon},
];

const CHATBOT_THREADS_STATUS_OPTIONS: {
    label: string;
    value: string;
    icon: React.ComponentType<any>;
}[] = [
    {label: 'Active', value: 'active', icon: ChatBubbleDotRegularIcon},
    {label: 'Unidentified', value: 'unidentified', icon: ChatBubbleQuestionRegularIcon},
    {label: 'Inactive', value: 'inactive', icon: ChatBubbleSlashRegularIcon},
];

export const getThreadStatusOptions = (
    currentSection: string,
    stats: InboxThreadCounts
): {
    label: string;
    value: string;
    icon: React.ComponentType<any>;
}[] => {
    // For chatbot-threads, use the special tabs
    if (currentSection === 'chatbot-threads') {
        return CHATBOT_THREADS_STATUS_OPTIONS;
    }

    return THREAD_STATUS_OPTIONS.filter((option) => {
        const count = getStatusCount(currentSection, stats, option.value.toLowerCase());

        // Always show Open and Closed statuses
        if (option.value === 'OPEN' || option.value === 'CLOSED') {
            return true;
        }

        // Filter out zero-count statuses for WAITING, SCHEDULED, SNOOZED, and FAILED
        return count;
    });
};

type ThreadCounts =
    | InboxThreadCountsChat
    | InboxThreadCountsSms
    | InboxThreadCountsMessenger
    | InboxThreadCountsInstagram
    | InboxThreadCountsAllChannels
    | InboxThreadCountsWhatsApp
    | InboxThreadCountsEmail
    | InboxThreadCountsChatbot;

// Get the appropriate thread counts based on section
const getThreadCounts = (currentSection: string, stats: InboxThreadCounts): ThreadCounts | null => {
    switch (currentSection) {
        case 'chat':
            return stats.chat;
        case 'sms':
            return stats.sms;
        case 'messenger':
            return stats.messenger;
        case 'all':
            return stats.allChannels;
        case 'instagram':
            return stats.instagram;
        case 'whatsapp':
            return stats.whatsapp;
        case 'email':
            return stats.email;
        case 'chatbot-threads':
            return stats.chatbot;
        default:
            return null;
    }
};

export const getStatusCount = (
    currentSection: string,
    stats: InboxThreadCounts,
    optionValue: string
): number | null => {
    if (!stats) return null;

    const threadCounts = getThreadCounts(currentSection, stats);
    if (!threadCounts) return null;

    // Special cases for SMS-only statuses
    if (currentSection === 'sms') {
        switch (optionValue) {
            case 'scheduled':
                return stats.sms?.scheduledCount ?? 0;
            case 'snoozed':
                return stats.sms?.snoozedCount ?? 0;
            case 'failed':
                return stats.sms?.failedCount ?? 0;
            case 'archived':
                return stats.sms?.archivedCount ?? 0;
        }
    }
    if (currentSection === 'chatbot-threads') {
        switch (optionValue) {
            case 'active':
                return stats.chatbot?.openCount ?? 0;
            case 'unidentified':
                return stats.chatbot?.unidentifiedCount ?? 0;
            case 'inactive':
            default:
                return null;
        }
    }

    // Common statuses for all channels
    switch (optionValue) {
        case 'unassigned':
            return 'unassignedCount' in threadCounts ? threadCounts.unassignedCount : 0;
        case 'closed':
            return threadCounts.closedCount;
        case 'open':
            return threadCounts.openCount;
        default:
            return null;
    }
};

export const getThreadListTitle = (tab?: string | null): string => {
    switch (tab) {
        case 'open':
            return 'Open';
        case 'closed':
            return 'Closed';
        case 'scheduled':
            return 'Scheduled';
        case 'snoozed':
            return 'Snoozed';
        case 'archived':
            return 'Archived';
        case 'failed':
            return 'Failed to send';
        case 'unassigned':
            return 'Unassigned';
        case 'active':
            return 'Active';
        case 'unidentified':
            return 'Unidentified';
        case 'inactive':
            return 'Inactive';
        default:
            return 'Open';
    }
};

export const getOriginFromThreadType = (threadType?: ThreadType | null): OriginType => {
    switch (threadType) {
        case 'CHAT':
            return 'CHAT';
        case 'SMS':
            return 'SMS';
        case 'MESSENGER':
            return 'MESSENGER';
        case 'EMAIL':
            return 'EMAIL';
        default:
            return 'WEB';
    }
};
